/**
 * @file DouleurCoudeCrossfit.tsx
 * @summary Article de blog — DouleurCoudeCrossfit
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import ArticleLayout, {
  EnBref, QuandConsulter, AdaptationChistera, AdaptationTable, FAQList, FurtherReading, SourcesSection, buildArticleJsonLd,
} from "@/components/blog/BlogParts";

const URL = "https://crossfitchistera.fr/blog/douleur-coude-crossfit";
const TITLE = "Douleur au coude en CrossFit : épicondylite, épitrochléite, tendinopathie du triceps";
const DESC = "Mal au coude en CrossFit : causes courantes (épicondylite, épitrochléite, triceps), comment adapter pull-ups, kettlebell, dips. Approche kiné par CrossFit Chistera.";

const author = { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" };
const reviewer = { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" };

const faq = [
  { q: "Tendinite ou tendinopathie du coude, c'est pareil ?", a: "Le terme 'tendinite' suppose une inflammation. Or, sur des douleurs de coude chroniques, on trouve un défaut de remodelage du collagène plus qu'une vraie inflammation. On parle donc plutôt de tendinopathie. Voir notre article général sur la tendinopathie." },
  { q: "Le rack en front squat me fait mal aux coudes. Que faire ?", a: "Souvent un manque de mobilité épaule + thoracique + poignet qui force le coude. Travailler le rack avec sangles, lats stretch et thoracic mobility. Si la douleur est sur l'extérieur du coude, c'est probablement une épicondylite, à traiter en parallèle." },
  { q: "Combien de temps pour soigner une épicondylite ?", a: "8-16 semaines avec un protocole de renforcement excentrique (Tyler twist, wrist extensions excentriques) et adaptation des séances. Beaucoup plus long (6-12 mois) si on continue à reproduire la sollicitation à charge inchangée." },
  { q: "Les kettlebells sont-elles à éviter ?", a: "Pas du tout, mais on adapte la prise et la charge. Les KB swings américains avec lâcher en haut sont très traumatisants pour le coude — préférer le russian swing (mi-hauteur) le temps du traitement." },
  { q: "Une coudière aide-t-elle vraiment ?", a: "Effet symptomatique modéré, pas de bénéfice à long terme prouvé. Utile en dépannage pour une compétition ou une journée chargée, pas pour soigner. La rééducation reste le traitement principal." },
  { q: "Peut-on faire des pull-ups avec une épicondylite ?", a: "Oui, mais en strict, en volume réduit, avec une prise neutre (parallel grip) qui dérange moins le coude. On évite le kipping et les volumes élevés (>50/séance) pendant 6-8 semaines." },
];

const adaptations = [
  { standard: "Kipping pull-ups", adapted: "Strict pull-ups, prise neutre, volume réduit" },
  { standard: "Bar muscle-ups", adapted: "Ring rows + dips séparés" },
  { standard: "American kettlebell swings", adapted: "Russian swings (mi-hauteur)" },
  { standard: "Snatch lourd", adapted: "Hang snatch léger ou high pulls" },
  { standard: "Cleans avec rebond rapide au rack", adapted: "Cleans avec pause au rack, charge modérée" },
  { standard: "Push-ups au sol", adapted: "Push-ups sur kettlebells (poignet neutre)" },
  { standard: "Dips lourds", adapted: "Dips assistés, ou bench dips" },
  { standard: "Farmer carry maximaux", adapted: "Farmer carry charge modérée, série courte" },
];

export default function DouleurCoudeCrossfit() {
  return (
    <ArticleLayout
      url={URL} title={TITLE} description={DESC}
      h1="Douleur au coude en CrossFit : adapter sans abandonner"
      breadcrumbLabel="Douleur au coude"
      category="Sport santé · Douleur ciblée"
      author={author} reviewer={reviewer}
      publishedAt="17 novembre 2026" updatedAt="17 novembre 2026" readingMinutes={8}
      ctaVariant="douleur" needsHealthDisclaimer
      jsonLd={buildArticleJsonLd({ url: URL, title: TITLE, description: DESC, publishedAt: "2026-11-17", author, reviewer, faq })}
    >
      <EnBref>
        <p>
          Le coude souffre rarement seul : c'est généralement <strong>un coude qui paie pour un poignet, une épaule ou une mobilité thoracique insuffisante</strong>. Trois grandes causes en CrossFit : l'épicondylite (face externe), l'épitrochléite (face interne), et la tendinopathie du triceps.
        </p>
        <p>
          La bonne nouvelle : ces tendinopathies répondent très bien à un protocole de renforcement progressif spécifique, à condition d'adapter en parallèle les mouvements déclencheurs.
        </p>
      </EnBref>

      <h2>Pourquoi le coude souffre en CrossFit</h2>
      <p><strong>Volume soudain et répétitif.</strong> Une compétition Open avec 100 toes-to-bar et 75 pull-ups, plus du kettlebell, et le coude qui n'avait jamais autant tiré déclenche une épicondylite la semaine suivante.</p>
      <p><strong>Manque de mobilité en amont.</strong> Un rack de front squat avec poignet bloqué (manque mobilité épaule) reporte la contrainte sur le coude. Pareil pour le push press si l'épaule manque de rotation externe.</p>
      <p><strong>Prise serrée et grip excessif.</strong> Beaucoup de pratiquants serrent la barre trop fort dans les pull-ups, le rowing, le DL. Cette tension permanente sur les fléchisseurs du poignet finit par enflammer l'attache au coude.</p>

      <h2>Les douleurs de coude les plus fréquentes</h2>
      <h3>L'épicondylite latérale (tennis elbow)</h3>
      <p>Douleur sur la face <em>externe</em> du coude, à l'attache des extenseurs du poignet. Aggravée par les pull-ups, le DL avec prise pronation, le grip prolongé. Test simple : douleur en serrant le poing avec poignet en extension contre résistance.</p>
      <p>Traitement : protocole excentrique (Tyler twist avec FlexBar) pendant 8-12 semaines, adaptation des prises, baisse temporaire du volume de pull-ups et de DL.</p>

      <h3>L'épitrochléite (golfer's elbow)</h3>
      <p>Douleur sur la face <em>interne</em> du coude, à l'attache des fléchisseurs du poignet. Souvent liée aux KB swings américains, aux kipping pull-ups avec arrachage au point haut, aux farmer carry maximaux.</p>
      <p>Traitement : excentrique des fléchisseurs du poignet, mobilité du grand pectoral et du grand dorsal, adaptation des KB swings (russian au lieu d'américain).</p>

      <h3>La tendinopathie du triceps</h3>
      <p>Douleur en arrière du coude, à la pointe de l'olécrâne. Aggravée par les dips, le bench press lourd, le push press répété. Souvent associée à un volume élevé de poussée.</p>
      <p>Traitement : excentrique du triceps (skull crushers tempo lent), pause des dips lourds, retour progressif.</p>

      <h3>Le coude "ouvert" en hyperextension</h3>
      <p>Plus rare, lié à un kipping mal contrôlé ou à un snatch / OHS où le coude se verrouille en hyperextension. Sensation d'à-coup. Travailler la chaîne d'absorption et la stabilité scapulaire.</p>

      <QuandConsulter>
        <p>Avis médical ou kiné sans délai si :</p>
        <ul>
          <li>Douleur après une chute ou un traumatisme direct</li>
          <li>Sensation de claquement audible avec impuissance fonctionnelle</li>
          <li>Gonflement marqué et hématome</li>
          <li>Verrouillage articulaire (impossible de tendre ou plier complètement le bras)</li>
          <li>Fourmillements dans la main ou les doigts (suspicion de compression nerveuse)</li>
          <li>Douleur qui ne s'améliore pas après 6 semaines de protocole bien mené</li>
        </ul>
      </QuandConsulter>

      <h2>Comment adapter ses séances</h2>
      <AdaptationTable rows={adaptations} />

      <h2>Le travail spécifique à intégrer</h2>
      <p><strong>Pour épicondylite</strong> : Tyler twist avec FlexBar (ou élastique), 3 séries de 15 répétitions, 1 fois par jour, 8-12 semaines. Compléter par des étirements en flexion + extension du poignet (30 secondes, 3 fois).</p>
      <p><strong>Pour épitrochléite</strong> : Reverse Tyler twist (rotation inverse du FlexBar). Même protocole.</p>
      <p><strong>Pour le triceps</strong> : skull crushers tempo 3-0-3 charge légère, 3 séries de 10, 2 fois par semaine.</p>
      <p>Voir aussi notre <Link to="/blog/tendinopathie-sport-crossfit">guide général sur la tendinopathie</Link> pour comprendre la logique de la charge progressive.</p>

      <AdaptationChistera>
        <p>Pour un adhérent avec douleur de coude :</p>
        <ol>
          <li>Le coach identifie le côté (externe / interne / postérieur) et le geste déclencheur</li>
          <li>Il adapte les pull-ups, KB swings, DL, dips selon le diagnostic</li>
          <li>Il intègre 1-2 exercices ciblés (Tyler twist, excentrique du poignet) en fin de séance</li>
          <li>Vérification de la technique du rack et du grip, souvent en cause</li>
          <li>Si la douleur dure plus de 6 semaines malgré l'adaptation, orientation kiné</li>
        </ol>
      </AdaptationChistera>

      <FAQList items={faq.map((f) => ({ q: f.q, a: <p>{f.a}</p> }))} />

      <FurtherReading items={[
        { label: "Tendinopathie et sport", to: "/blog/tendinopathie-sport-crossfit" },
        { label: "Douleur à l'épaule en CrossFit", to: "/blog/douleur-epaule-crossfit" },
        { label: "Faire du sport avec une douleur", to: "/blog/faire-du-sport-avec-douleur" },
        { label: "Calculateur de charges 1RM", to: "/blog/calculateur-charges-crossfit-1rm" },
      ]} />

      <SourcesSection items={[
        { label: "Tyler et al. 2010 — Eccentric strengthening for lateral epicondylosis", href: "https://pubmed.ncbi.nlm.nih.gov/21037520/" },
        { label: "BJSM 2023 — Resistance exercise dose components for tendinopathy management", href: "https://bjsm.bmj.com/content/57/20/1327" },
        { label: "Coombes et al. 2013 — Effect of corticosteroid injection vs physiotherapy on lateral epicondylalgia (JAMA)", href: "https://pubmed.ncbi.nlm.nih.gov/23385272/" },
      ]} />
    </ArticleLayout>
  );
}
